import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionControlResultDetails } from '@connect-services/client/services/field-intervention.service';
import { FieldIntervention, FieldInterventionControlResult } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum ToggleValidEnum {
    INVALID = 'invalid',
    TO_VALIDATE = 'toValidate',
    VALID = 'valid',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-control-result-slide-toggle',
    styleUrl: './control-result-slide-toggle.component.scss',
    templateUrl: './control-result-slide-toggle.component.html',
})
export class ControlResultSlideToggleComponent implements OnInit {
    public readonly controlResult = input.required<FieldInterventionControlResultDetails>();
    public readonly validityUpdated = output<FieldInterventionControlResult.ValidationEnum>();

    protected readonly cannotInvalidate = computed<boolean>(
        () => this.isInterventionDone() && this.currentValidity() === ToggleValidEnum.VALID,
    );
    protected readonly currentValidity = signal<FieldInterventionControlResult.ValidationEnum>(ToggleValidEnum.VALID);
    protected readonly isInterventionDone = computed<boolean>(
        () => this.fieldInterventionBloc.value()?.status === FieldIntervention.StatusEnum.DONE,
    );
    protected readonly ToggleValidationEnum = ToggleValidEnum;

    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);

    public get icon(): IconProp {
        switch (this.currentValidity()) {
            case ToggleValidEnum.VALID:
                return ['fas', 'check'];

            case ToggleValidEnum.INVALID:
                return ['fas', 'xmark'];

            case ToggleValidEnum.TO_VALIDATE:
                return ['fas', 'business-time'];

            default:
                return ['fas', 'check'];
        }
    }

    public ngOnInit(): void {
        this.currentValidity.set(this.controlResult().validation);
    }

    public toggleValidation(): void {
        if (this.cannotInvalidate()) {
            return;
        }

        let newValidationStatus: FieldInterventionControlResult.ValidationEnum | null = null;

        switch (this.currentValidity()) {
            case ToggleValidEnum.VALID:
                newValidationStatus = ToggleValidEnum.INVALID;
                break;

            case ToggleValidEnum.INVALID:
            case ToggleValidEnum.TO_VALIDATE:
                newValidationStatus = ToggleValidEnum.VALID;
                break;
        }

        if (!newValidationStatus) {
            return;
        }

        this.currentValidity.set(newValidationStatus);
        this.validityUpdated.emit(newValidationStatus);
    }
}
